import SCRService from '@/services/SCRService.ts';
/**
 * SCR / CKE Vuex Store Module
 *
 * Hanterar:
 * - SCR-integrationsstatus (aktiv/inaktiv)
 * - Sökresultat från scanner/manuell input
 * - CKE-kortdata kopplad till kund i bokningsflödet
 */
export const namespaced = true;
export const state = {
    isActive: false,
    mode: null,
    usesProductionCredentials: false,
    organisationName: '',
    hasCredentials: false,
    cardProductId: null,
    statusChecked: false,
    searchResult: null,
    isSearching: false,
    pendingCardNumber: null
};
export const mutations = {
    SET_STATUS(state, payload) {
        state.isActive = payload.is_active ?? false;
        state.mode = payload.mode ?? null;
        state.usesProductionCredentials = payload.uses_production_credentials ?? false;
        state.organisationName = payload.organisation_name ?? '';
        state.hasCredentials = payload.has_credentials ?? false;
        state.cardProductId = payload.card_product_id ?? null;
        state.statusChecked = true;
    },
    SET_STATUS_INACTIVE(state) {
        state.isActive = false;
        state.hasCredentials = false;
        state.statusChecked = true;
    },
    SET_SEARCH_RESULT(state, val) {
        state.searchResult = val;
    },
    SET_IS_SEARCHING(state, val) {
        state.isSearching = val;
    },
    SET_PENDING_CARD_NUMBER(state, val) {
        state.pendingCardNumber = val;
    },
    CLEAR_SEARCH(state) {
        state.searchResult = null;
        state.isSearching = false;
        state.pendingCardNumber = null;
    }
};
export const actions = {
    /**
     * Kontrollera om SCR-integrationen är aktiverad.
     * Anropas efter inloggning (i fetchData-flödet).
     */
    fetchStatus({ commit }) {
        return SCRService.getStatus()
            .then(({ data }) => {
            if (data.status === 'success') {
                commit('SET_STATUS', data.data);
            }
            else {
                commit('SET_STATUS_INACTIVE');
            }
        })
            .catch(() => {
            // SCR-fel ska aldrig blockera appen
            commit('SET_STATUS_INACTIVE');
        });
    },
    /**
     * Smart sökning — anropas från bokningsflödet eller kundformuläret
     * när scanner skickar data eller användaren skriver in manuellt.
     *
     * Skickar rå input (oavkodad) till servern som hanterar alla format:
     * - Base64-encoded scanner data
     * - Kortnummer (13-16 siffror)
     * - Personnummer (10-12 siffror)
     * - E-post
     * - Namn
     */
    search({ commit }, query) {
        if (!query || query.trim() === '') {
            return Promise.resolve(null);
        }
        commit('SET_IS_SEARCHING', true);
        commit('SET_SEARCH_RESULT', null);
        return SCRService.search(query.trim())
            .then(({ data }) => {
            if (data.status === 'success') {
                const result = {
                    ...data.data,
                    query: query.trim()
                };
                commit('SET_SEARCH_RESULT', result);
                if (result.source !== 'none') {
                    commit('SET_PENDING_CARD_NUMBER', result.scr?.card_number ?? null);
                }
                return result;
            }
            return null;
        })
            .catch(() => {
            // SCR-fel ska aldrig blockera bokningsflödet
            return null;
        })
            .finally(() => {
            commit('SET_IS_SEARCHING', false);
        });
    },
    /**
     * Hanterar scanner-input från scannerVM.
     * Anropas via store dispatch från scannerVM respondTo-mekanismen.
     *
     * { input } = rå scanner-sträng (JSON, base64 eller siffror)
     */
    handleScannerInput({ dispatch, commit }, { input }) {
        if (!input || input.trim() === '') {
            return Promise.resolve(null);
        }
        return dispatch('search', input).then(result => {
            if (result === null) {
                commit('SET_SEARCH_RESULT', { source: 'none', query: input.trim() });
            }
            return result;
        });
    },
    /**
     * Rensa SCR-sökdata (t.ex. när bokning/kundformulär stängs)
     */
    clearSearch({ commit }) {
        commit('CLEAR_SEARCH');
    }
};
export const getters = {
    /**
     * Är CKE-integrationen aktiverad och redo att användas?
     */
    isEnabled(state) {
        return state.isActive && state.hasCredentials && state.statusChecked;
    },
    /**
     * Har vi sökresultat med en matchad kund (lokal eller SCR)?
     */
    hasCustomerResult(state) {
        if (!state.searchResult) {
            return false;
        }
        return state.searchResult.customer && state.searchResult.source !== 'none';
    },
    /**
     * Har vi ett SCR-kort i sökresultatet?
     */
    hasScrData(state) {
        if (!state.searchResult) {
            return false;
        }
        return !!state.searchResult.scr;
    },
    /**
     * Returnerar source från senaste sökningen
     */
    searchSource(state) {
        return state.searchResult?.source ?? null;
    }
};
